import { useState, useEffect } from "react";
import { FiUser, FiMail, FiSave, FiTrash2 } from "react-icons/fi";
import API from "../../api";

export default function Settings({ user }) {
    const [newFullName, setNewFullName] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // Popup suppression
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [showDeletedPopup, setShowDeletedPopup] = useState(false);

    useEffect(() => {
        if (user) {
        setNewFullName(user.fullName || "");
        setNewEmail(user.email || "");
        }
    }, [user]);

    const handleChangeFullName = (e) => setNewFullName(e.target.value);
    const handleChangeEmail = (e) => setNewEmail(e.target.value);

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        try {
        await API.put(`/modify/${user.id}`, {
            fullName: newFullName,
            email: newEmail,
        });
        setMessage("✅ Your settings have been updated successfully!");
        } catch (err) {
        console.error(err);
        setMessage("❌ An error occurred while updating your settings.");
        } finally {
        setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
        await API.delete(`/delete/${user.id}`);
        setShowDeletePopup(false);
        setShowDeletedPopup(true);

        // Redirige après 2 secondes
        setTimeout(() => {
            setShowDeletedPopup(false);
            window.location.href = "/";
        }, 2000);
        } catch (err) {
        console.error(err);
        setMessage("❌ An error occurred while deleting your account.");
        }
    };

    return (
        <div className="min-h-screen w-full bg-[#0D1117] flex flex-col items-center justify-start px-6 py-16 text-[#E6EDF3]">
    
        <div className="bg-[#161B22] border border-[#21262D] rounded-xl p-10 w-full max-w-xl">
    
            <h1 className="text-3xl font-semibold text-center mb-10 tracking-tight">
            Account Settings
            </h1>
    
            <form onSubmit={handleSave} className="flex flex-col gap-6">
    
            {/* Full Name */}
            <div className="flex flex-col gap-1.5">
                <label className="text-[#9BA6B3] text-sm font-medium">
                Full Name
                </label>
                <div className="relative">
                <FiUser className="absolute top-3 left-3 text-[#6E7681]" size={18} />
                <input
                    type="text"
                    value={newFullName}
                    onChange={handleChangeFullName}
                    className="bg-[#0D1117] border border-[#30363D] rounded-md pl-10 pr-3 py-2.5 w-full focus:outline-none focus:border-[#3B82F6] placeholder-[#6E7681]"
                    placeholder="John Doe"
                    required
                />
                </div>
            </div>
    
            {/* Email */}
            <div className="flex flex-col gap-1.5">
                <label className="text-[#9BA6B3] text-sm font-medium">
                Email
                </label>
                <div className="relative">
                <FiMail className="absolute top-3 left-3 text-[#6E7681]" size={18} />
                <input
                    type="email"
                    value={newEmail}
                    onChange={handleChangeEmail}
                    className="bg-[#0D1117] border border-[#30363D] rounded-md pl-10 pr-3 py-2.5 w-full focus:outline-none focus:border-[#3B82F6] placeholder-[#6E7681]"
                    placeholder="example@mail.com"
                    required
                />
                </div>
            </div>
    
            {/* Save Button */}
            <button
                type="submit"
                disabled={loading}
                className="bg-[#3B82F6] hover:bg-[#2563EB] disabled:opacity-60 disabled:cursor-not-allowed
                text-white font-medium py-3 rounded-md transition flex items-center justify-center gap-2"
            >
                <FiSave size={18} />
                {loading ? "Saving..." : "Save Changes"}
            </button>
    
            {/* Message */}
            {message && (
                <p
                className={`text-center font-medium mt-2 ${
                    message.startsWith("✅") ? "text-green-500" : "text-red-500"
                }`}
                >
                {message}
                </p>
            )}
            </form>
    
            {/* Divider */}
            <div className="mt-8 border-b border-[#21262D]"></div>

            {/* Billing Section */}
            <div className="mt-8 flex flex-col gap-4">
            <h2 className="text-xl font-semibold text-[#E6EDF3]">Billing</h2>

            {user?.billing && user.billing.length > 0 ? (
                <div className="bg-[#0D1117] border border-[#30363D] rounded-lg p-4 flex flex-col gap-3">
                
                {user.billing
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((bill, index) => (
                    <div key={index} className="flex justify-between items-center border-b border-[#21262D] pb-2 last:border-0 last:pb-0">
                        
                        <div>
                        <p className="text-sm text-[#E6EDF3] font-medium">
                            {bill.amount} €
                        </p>
                        <p className="text-xs text-[#6E7681]">
                            {new Date(bill.date).toLocaleDateString()}
                        </p>
                        </div>

                        {/* Future: Download invoice button (Stripe) */}
                        {/* <button className="text-xs text-[#3B82F6] hover:underline">Receipt</button> */}
                    </div>
                    ))}

                </div>
            ) : (
                <p className="text-sm text-[#8B949E]">No payments recorded yet.</p>
            )}
            </div>

            {/* Delete Button */}
            <button
                onClick={() => setShowDeletePopup(true)}
                className="mt-8 bg-[#1F2A37] hover:bg-[#273340] text-white font-medium py-3 w-full rounded-md transition flex items-center justify-center gap-2"
                >
                <FiTrash2 size={18} />
                Delete My Account
            </button>
        </div>
    
        {/* Popup confirmation */}
        {showDeletePopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-[#161B22] p-8 rounded-lg border border-[#21262D] max-w-md w-11/12 text-center">
            <p className="text-lg font-medium mb-6 leading-relaxed">
                Permanently delete your account?<br />
                This action cannot be undone.
            </p>
    
            <div className="flex justify-center gap-4">
                <button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white py-2 px-6 rounded-md font-medium transition"
                >
                Delete
                </button>
                <button
                onClick={() => setShowDeletePopup(false)}
                className="bg-[#30363D] hover:bg-[#3A424C] text-white py-2 px-6 rounded-md font-medium transition"
                >
                Cancel
                </button>
            </div>
            </div>
        </div>
        )}
    
        {/* Popup deleted */}
        {showDeletedPopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-[#161B22] p-8 rounded-lg border border-[#21262D] max-w-md w-11/12 text-center">
            <p className="text-xl font-bold mb-3 text-green-400">
                ✅ Account Deleted
            </p>
            <p className="text-[#9BA6B3] text-sm">We’re sorry to see you leave.</p>
            </div>
        </div>
        )}
    
    </div>
    );
    
}
